import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CloseSessionInput, FeeQuoteDto } from '@parking/shared';
import { api } from '../shared/api/client';
import { useAuthStore } from '../shared/store/authStore';
import { enumLabel } from '../shared/i18n/enumLabel';
import { Card } from '../shared/ui/Card';
import { ParkingDataTable } from '../shared/ui/ParkingDataTable';
import { AppModal } from '../shared/ui/AppModal';
import { StatusBadge, statusToneFromEnum } from '../shared/ui/StatusBadge';

export function SessionsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canRead = useAuthStore((s) => s.hasPermission('sessions:read'));
  const canWrite = useAuthStore((s) => s.hasPermission('sessions:write'));
  const canPos = useAuthStore((s) => s.hasPermission('pos:operate'));
  const [lastInvoice, setLastInvoice] = useState<string | null>(null);

  const [entryDialog, setEntryDialog] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);
  const [parkingId, setParkingId] = useState('');
  const [plate, setPlate] = useState('');
  const [spotId, setSpotId] = useState('');
  const [closingId, setClosingId] = useState('');
  const [paymentMethod, setPaymentMethod] =
    useState<NonNullable<CloseSessionInput['paymentMethod']>>('CASH');
  const [quote, setQuote] = useState<FeeQuoteDto | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'OPEN' | 'CLOSED' | 'ALL'>('OPEN');

  const parkings = useQuery({
    queryKey: ['parkings'],
    queryFn: () => api.listParkings(),
    enabled: canRead,
  });

  const activeParking = parkingId || parkings.data?.data[0]?.id || '';

  const spots = useQuery({
    queryKey: ['spots', activeParking],
    queryFn: () => api.listSpots(activeParking),
    enabled: entryDialog && Boolean(activeParking),
  });

  const availableSpots = useMemo(
    () => (spots.data?.data ?? []).filter((s) => s.status === 'AVAILABLE'),
    [spots.data],
  );

  const sessions = useQuery({
    queryKey: ['sessions', filter],
    queryFn: () =>
      filter === 'ALL' ? api.listSessions() : api.listSessions({ status: filter }),
    enabled: canRead,
    refetchInterval: 15_000,
  });

  const openEntry = useMutation({
    mutationFn: () =>
      api.openSession({
        parkingId: activeParking,
        plate: plate.trim(),
        spotId: spotId || null,
      }),
    onSuccess: async () => {
      setEntryDialog(false);
      setPlate('');
      setSpotId('');
      setFormError(null);
      await qc.invalidateQueries({ queryKey: ['sessions'] });
      await qc.invalidateQueries({ queryKey: ['spots'] });
      await qc.invalidateQueries({ queryKey: ['parkings'] });
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const prepareClose = async (sessionId: string, parking: string, entryAt: string) => {
    setClosingId(sessionId);
    setFormError(null);
    setQuote(null);
    setCloseDialog(true);
    try {
      const res = await api.quoteSession({
        parkingId: parking,
        entryAt,
        exitAt: new Date().toISOString(),
      });
      setQuote(res.data);
    } catch (e) {
      setFormError((e as Error).message);
    }
  };

  const closeSession = useMutation({
    mutationFn: async () => {
      if (canPos) {
        return api.posCharge({
          sessionId: closingId,
          paymentMethod: paymentMethod ?? 'CASH',
        });
      }
      return api.closeSession(closingId, {
        paymentMethod,
        issueInvoice: true,
      });
    },
    onSuccess: async (res) => {
      setCloseDialog(false);
      setClosingId('');
      setQuote(null);
      const inv =
        'invoice' in res.data
          ? res.data.invoice
          : (res.data as { invoice?: { fullNumber: string } }).invoice;
      setLastInvoice(inv?.fullNumber ?? null);
      await qc.invalidateQueries({ queryKey: ['sessions'] });
      await qc.invalidateQueries({ queryKey: ['spots'] });
      await qc.invalidateQueries({ queryKey: ['parkings'] });
      await qc.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const cancelSession = useMutation({
    mutationFn: (id: string) => api.cancelSession(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['sessions'] });
      await qc.invalidateQueries({ queryKey: ['spots'] });
      await qc.invalidateQueries({ queryKey: ['parkings'] });
    },
  });

  if (!canRead) {
    return <div className="alert alert-warning">{t('sessions.forbidden')}</div>;
  }

  const rows = sessions.data?.data ?? [];
  const columns = [
    t('sessions.plate'),
    t('sessions.parking'),
    t('sessions.spot'),
    t('sessions.status'),
    t('sessions.entryAt'),
    t('sessions.duration'),
    t('sessions.amount'),
    ...(canWrite ? [t('common.actions')] : []),
  ];

  return (
    <>
      <div className="row g-3 align-items-end mb-4">
        <div className="col-12 col-md-auto">
          <label className="form-label" htmlFor="sess-filter">
            {t('sessions.filter')}
          </label>
          <select
            id="sess-filter"
            className="form-select"
            style={{ minWidth: 200 }}
            value={filter}
            onChange={(e) => setFilter(e.target.value as typeof filter)}
          >
            <option value="OPEN">{t('sessions.filterOpen')}</option>
            <option value="CLOSED">{t('sessions.filterClosed')}</option>
            <option value="ALL">{t('sessions.filterAll')}</option>
          </select>
        </div>
        {canWrite ? (
          <div className="col-12 col-md-auto ms-md-auto">
            <button
              type="button"
              className="btn btn-primary w-100"
              onClick={() => {
                setFormError(null);
                setPlate('');
                setSpotId('');
                setParkingId(parkings.data?.data[0]?.id ?? '');
                setEntryDialog(true);
              }}
            >
              {t('sessions.open')}
            </button>
          </div>
        ) : null}
      </div>

      {sessions.isError && (
        <div className="alert alert-danger">{(sessions.error as Error).message}</div>
      )}
      {lastInvoice && (
        <div className="alert alert-success d-flex justify-content-between align-items-center">
          <span>{t('sessions.invoiceIssued', { number: lastInvoice })}</span>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setLastInvoice(null)}
          />
        </div>
      )}

      <Card>
        <ParkingDataTable
          storageKey="sessions"
          columns={columns}
          refreshKey={`${filter}|${rows.map((s) => `${s.id}:${s.status}`).join('|')}`}
          order={[[4, 'desc']]}
          actionsColumn={canWrite}
          galleryItems={rows.map((s) => {
            const duration =
              s.durationMinutes != null
                ? `${s.durationMinutes} min`
                : `${Math.max(1, Math.ceil((Date.now() - new Date(s.entryAt).getTime()) / 60000))} min`;
            return {
              id: s.id,
              icon: 'bi-ticket-perforated',
              iconTone: statusToneFromEnum(s.status),
              title: s.plate,
              subtitle: `${s.parkingCode ?? s.parkingId.slice(0, 8)} · ${s.spotCode ?? '—'}`,
              meta: `${new Date(s.entryAt).toLocaleString()} · ${duration} · ${s.amountCents != null ? `${(s.amountCents / 100).toFixed(2)} €` : '—'}`,
              badge: (
                <StatusBadge tone={statusToneFromEnum(s.status)}>
                  {enumLabel(t, 'sessionStatus', s.status)}
                </StatusBadge>
              ),
              actions:
                canWrite && s.status === 'OPEN' ? (
                  <div className="d-flex gap-1 justify-content-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => void prepareClose(s.id, s.parkingId, s.entryAt)}
                    >
                      {t('sessions.close')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => {
                        if (window.confirm(t('sessions.confirmCancel'))) {
                          cancelSession.mutate(s.id);
                        }
                      }}
                    >
                      {t('sessions.cancel')}
                    </button>
                  </div>
                ) : undefined,
            };
          })}
        >
          {rows.map((s) => (
            <tr key={s.id}>
              <td className="fw-semibold">{s.plate}</td>
              <td>{s.parkingCode ?? s.parkingId.slice(0, 8)}</td>
              <td>{s.spotCode ?? '—'}</td>
              <td>
                <StatusBadge tone={statusToneFromEnum(s.status)}>
                  {enumLabel(t, 'sessionStatus', s.status)}
                </StatusBadge>
              </td>
              <td>{new Date(s.entryAt).toLocaleString()}</td>
              <td>
                {s.durationMinutes != null
                  ? `${s.durationMinutes} min`
                  : `${Math.max(1, Math.ceil((Date.now() - new Date(s.entryAt).getTime()) / 60000))} min`}
              </td>
              <td>
                {s.amountCents != null ? `${(s.amountCents / 100).toFixed(2)} €` : '—'}
              </td>
              {canWrite && (
                <td>
                  {s.status === 'OPEN' && (
                    <div className="d-flex gap-1 justify-content-center">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => void prepareClose(s.id, s.parkingId, s.entryAt)}
                      >
                        {t('sessions.close')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-light"
                        onClick={() => {
                          if (window.confirm(t('sessions.confirmCancel'))) {
                            cancelSession.mutate(s.id);
                          }
                        }}
                      >
                        {t('sessions.cancel')}
                      </button>
                    </div>
                  )}
                </td>
              )}
            </tr>
          ))}
        </ParkingDataTable>
      </Card>

      <AppModal
        open={entryDialog}
        onClose={() => setEntryDialog(false)}
        title={t('sessions.open')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={() => setEntryDialog(false)}>
              {t('sessions.cancelBtn')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!plate.trim() || !activeParking || openEntry.isPending}
              onClick={() => openEntry.mutate()}
            >
              {t('sessions.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          {formError && <div className="alert alert-danger mb-0">{formError}</div>}
          <div>
            <label className="form-label">{t('sessions.parking')}</label>
            <select
              className="form-select"
              value={activeParking}
              onChange={(e) => {
                setParkingId(e.target.value);
                setSpotId('');
              }}
            >
              {(parkings.data?.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">{t('sessions.plate')}</label>
            <input
              className="form-control"
              value={plate}
              onChange={(e) => setPlate(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="form-label">{t('sessions.spot')}</label>
            <select
              className="form-select"
              value={spotId}
              onChange={(e) => setSpotId(e.target.value)}
            >
              <option value="">—</option>
              {availableSpots.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} ({enumLabel(t, 'spotType', s.type)})
                </option>
              ))}
            </select>
          </div>
        </div>
      </AppModal>

      <AppModal
        open={closeDialog}
        onClose={() => setCloseDialog(false)}
        title={t('sessions.close')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={() => setCloseDialog(false)}>
              {t('sessions.cancelBtn')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={closeSession.isPending}
              onClick={() => closeSession.mutate()}
            >
              {t('sessions.confirmClose')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          {formError && <div className="alert alert-danger mb-0">{formError}</div>}
          {quote && (
            <div className={`alert mb-0 ${quote.isSubscriber ? 'alert-success' : 'alert-info'}`}>
              {quote.isSubscriber
                ? t('sessions.subscriberFree')
                : t('sessions.quote', {
                    minutes: quote.durationMinutes,
                    amount: (quote.amountCents / 100).toFixed(2),
                    tariff: quote.tariffCode ?? '—',
                  })}
            </div>
          )}
          <div>
            <label className="form-label">{t('sessions.payment')}</label>
            <select
              className="form-select"
              value={paymentMethod ?? 'CASH'}
              onChange={(e) =>
                setPaymentMethod(e.target.value as NonNullable<CloseSessionInput['paymentMethod']>)
              }
            >
              {['CASH', 'CARD', 'BIZUM', 'TRANSFER'].map((m) => (
                <option key={m} value={m}>
                  {enumLabel(t, 'paymentMethod', m)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </AppModal>
    </>
  );
}
